import axios, { AxiosInstance } from 'axios';

// API base URL - Hardcoded for production
const getApiBaseUrl = () => {
    const hostname = window.location.hostname;

    // Production domains - no port (behind reverse proxy)
    if (hostname === 'lima.ac' || hostname.includes('lima.ac')) {
        return 'https://lima.ac/api';
    }
    if (hostname === 'lima.pitech.ca' || hostname.includes('lima.pitech.ca')) {
        return 'https://lima.pitech.ca/api';
    }
    if (hostname === 'bagbox.ca' || hostname.includes('bagbox.ca')) {
        return 'https://bagbox.ca/api';
    }

    // Development/localhost
    return 'http://localhost:80/api';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config
export const backendAPI: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token and tenant hostname
backendAPI.interceptors.request.use(
    (config) => {
        // Add auth token
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Add tenant hostname (required for multi-tenancy)
        const hostname = window.location.hostname;
        config.headers['x-tenant-host'] = hostname;

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
backendAPI.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

/**
 * Helper function to get full media URL from relative path
 * Works for images, podcasts, videos, and other media files
 * @param relativePath - Relative path from backend (e.g., "/api/upload/tenant-id/media/image.jpg" or "/podcasts/podcast-xxx.mp3")
 * @returns Full URL to the media file
 */
export const getImageUrl = (relativePath: string | undefined | null): string => {
    if (!relativePath) {
        return '/placeholder-image.jpg'; // Default placeholder for images only
    }

    // If already a full URL, return as-is
    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
        return relativePath;
    }

    // If starts with /api, it's already a complete backend path
    // This includes tenant-specific paths like /api/upload/{tenantId}/media/filename
    if (relativePath.startsWith('/api')) {
        return relativePath; // Return as-is - already includes /api prefix
    }

    // If starts with /podcasts or /music, these are shared public resources (no tenant ID)
    // Return as-is - they're served directly from /public directory
    if (relativePath.startsWith('/podcasts') || relativePath.startsWith('/music')) {
        return relativePath;
    }

    // If starts with /upload (but not /api/upload), prepend /api
    // This handles legacy paths that don't include /api prefix
    if (relativePath.startsWith('/upload')) {
        return `/api${relativePath}`;
    }

    // Get backend URL based on hostname
    const hostname = window.location.hostname;
    let backendUrl;

    // Production domains - no port (behind reverse proxy)
    if (hostname === 'lima.ac' || hostname.includes('lima.ac')) {
        backendUrl = 'https://lima.ac';
    } else if (hostname === 'lima.pitech.ca' || hostname.includes('lima.pitech.ca')) {
        backendUrl = 'https://lima.pitech.ca';
    } else if (hostname === 'bagbox.ca' || hostname.includes('bagbox.ca')) {
        backendUrl = 'https://bagbox.ca';
    } else {
        // Development/localhost
        backendUrl = 'http://localhost:80';
    }

    // Otherwise, assume it's a relative path that needs /api/upload prefix
    return `${backendUrl}/api/upload/${relativePath}`;
};

export default backendAPI;

